/**
 * The Brain's data layer. Same shape as `documents-api.ts`.
 *
 * The list key carries its filters, so changing a filter is a new query rather
 * than a refetch of the same one — which is what keeps the previous page on
 * screen while the next loads instead of flashing empty.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  BrainItem,
  DocumentAsset,
  DocumentSegment,
  FileStatus,
  KnowledgeEntry,
  KnowledgeNoteInput,
  KnowledgeSourceType,
  KnowledgeSummary,
  ProjectDocument,
  ProjectOrganise,
} from "@gb/contracts";
import { api, errorMessage } from "@/api/client";

export type BrainFilters = {
  q?: string;
  sourceType?: KnowledgeSourceType;
  /**
   * One of the tenant's categories, by key: the files whose main category it
   * is, a category shelf's own list (`document-ai/0032`). Lists documents
   * only.
   */
  category?: string;
  /**
   * One category's key: the files that live in another category and list
   * this one, the shelf's folded "Also relevant here" group. Lists documents
   * only.
   */
  relevant?: string;
  /** `"no"` for the files with no main category: the "Not sorted yet" shelf. Lists documents only. */
  sorted?: "no";
  /**
   * `"open"` for the files with a question waiting for a person: the "Needs a
   * decision" shelf. A placement, a hide the check doubts, or a link from the
   * file (every question since 2026-09-26). Lists documents only.
   */
  decision?: "open";
  /**
   * One file status (`document-ai/0013`). Lists documents only, and it is the
   * **one way a hidden file is listed at all**: a file the check flagged is in
   * no ordinary list and in no search, so without this filter a person could
   * not reach the files waiting on them. For a hidden status, the files the
   * check asks about with it come first (2026-09-26, HID-11).
   */
  status?: FileStatus;
  /**
   * `"no"` for the files an organise step has not done yet: the "Not
   * organised yet" shelf (2026-09-26, RET-12). Lists documents only.
   */
  organised?: "no";
  /** `"yes"` for the files removed at their source: the "Removed at source" shelf (2026-10-18). Lists documents only. */
  removed?: "yes";
  limit?: number;
  offset?: number;
};

export const brainKeys = {
  summary: (projectId: string) =>
    ["project", projectId, "brain", "summary"] as const,
  items: (projectId: string, filters: BrainFilters) =>
    ["project", projectId, "brain", "items", filters] as const,
  detail: (projectId: string, entryId: string) =>
    ["project", projectId, "brain", "entries", entryId] as const,
  document: (projectId: string, documentId: string) =>
    ["project", projectId, "brain", "document", documentId] as const,
  segments: (projectId: string, documentId: string) =>
    [
      "project",
      projectId,
      "brain",
      "document",
      documentId,
      "segments",
    ] as const,
  assets: (projectId: string, documentId: string) =>
    ["project", projectId, "brain", "document", documentId, "assets"] as const,
};

/**
 * How often a still-parsing document is re-asked about.
 *
 * Extraction is minutes on a scan-heavy PDF, so this is not a progress bar —
 * it is the difference between a row that says "Reading…" and then becomes a
 * readable document, and one that silently never changes. Polling stops the
 * moment nothing is in flight.
 */
const PARSE_POLL_MS = 4_000;

/** Whether anything on screen is still being read, and so worth re-asking. */
function anyParsing(items: readonly BrainItem[]): boolean {
  return items.some(
    (item) =>
      item.kind === "document" &&
      (item.processingStatus === "queued" ||
        item.processingStatus === "running"),
  );
}

/**
 * How often the summary is re-asked while the project's own organise run is
 * queued or running (2026-09-26, RET-04). A run can wait five minutes to
 * start, so this is slower than a parse's poll.
 */
const ORGANISE_POLL_MS = 15_000;

export function useBrainSummary(projectId: string | undefined) {
  return useQuery({
    queryKey: brainKeys.summary(projectId ?? ""),
    enabled: Boolean(projectId),
    // The shelves change when the run ends, and nothing else would re-ask.
    refetchInterval: (query) => {
      const state = query.state.data?.organise.state;
      return state === "queued" || state === "running" ? ORGANISE_POLL_MS : false;
    },
    queryFn: async (): Promise<KnowledgeSummary> => {
      const res = await api.brain.summary({ params: { id: projectId! } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
}

/**
 * "Organise now" (2026-09-26, RET-04): the project's own organise run, for the
 * files no import has organised. A run already queued is the answer, not a
 * second one. The Brain is re-read after it, since the state and the shelves
 * move.
 */
export function useOrganiseProject(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<ProjectOrganise> => {
      const res = await api.projects.organiseProject({ params: { id: projectId }, body: {} });
      if (res.status !== 202) throw new Error(errorMessage(res));
      return res.body.organise;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["project", projectId, "brain"] });
    },
  });
}

export function useBrainItems(
  projectId: string | undefined,
  filters: BrainFilters,
) {
  return useQuery({
    queryKey: brainKeys.items(projectId ?? "", filters),
    enabled: Boolean(projectId),
    // Keeps the current rows visible while the next filter loads.
    placeholderData: (previous) => previous,
    // A document appears the instant its bytes land and is read afterwards, so
    // the list re-asks while anything is still in flight and goes quiet when
    // nothing is. Without this an upload sits saying "Reading…" until somebody
    // reloads the page, which reads exactly like a stuck upload.
    refetchInterval: (query) =>
      anyParsing(query.state.data?.items ?? []) ? PARSE_POLL_MS : false,
    queryFn: async (): Promise<{ items: BrainItem[]; total: number }> => {
      const res = await api.brain.list({
        params: { id: projectId! },
        query: {
          // Empty strings are omitted rather than sent: `q=""` is a filter that
          // matches everything, which is not what "no filter" means.
          ...(filters.q ? { q: filters.q } : {}),
          ...(filters.sourceType ? { sourceType: filters.sourceType } : {}),
          ...(filters.category ? { category: filters.category } : {}),
          ...(filters.relevant ? { relevant: filters.relevant } : {}),
          ...(filters.sorted ? { sorted: filters.sorted } : {}),
          ...(filters.decision ? { decision: filters.decision } : {}),
          ...(filters.status ? { status: filters.status } : {}),
          ...(filters.organised ? { organised: filters.organised } : {}),
          ...(filters.removed ? { removed: filters.removed } : {}),
          limit: filters.limit ?? 50,
          offset: filters.offset ?? 0,
        },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
}

// ------------------------------------------------------------------
// One document, for the viewer
// ------------------------------------------------------------------

export function useBrainDocument(
  projectId: string | undefined,
  documentId: string | undefined,
) {
  return useQuery({
    queryKey: brainKeys.document(projectId ?? "", documentId ?? ""),
    enabled: Boolean(projectId && documentId),
    // Same reason as the list: the header says "Reading…" and has to stop.
    refetchInterval: (query) => {
      const status = query.state.data?.processingStatus;
      return status === "queued" || status === "running"
        ? PARSE_POLL_MS
        : false;
    },
    queryFn: async (): Promise<ProjectDocument> => {
      const res = await api.projects.getDocument({
        params: { id: projectId!, docId: documentId! },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.document;
    },
  });
}

/**
 * The document's citable surfaces — pages, or sheets.
 *
 * This is the whole viewer for a DOCX or an XLSX, neither of which a browser
 * can render, and the text panel beside a PDF. An empty array is a legitimate
 * answer (a DWG, or a parse that has not run), so the screen reads
 * `processingStatus` to decide whether that means "not readable" or "not yet".
 */
export function useDocumentSegments(
  projectId: string | undefined,
  documentId: string | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: brainKeys.segments(projectId ?? "", documentId ?? ""),
    enabled: Boolean(projectId && documentId) && options?.enabled !== false,
    queryFn: async (): Promise<DocumentSegment[]> => {
      const res = await api.projects.listSegments({
        params: { id: projectId!, docId: documentId! },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.segments;
    },
  });
}

/** Figures and scans lifted out of the document. `decoration` is not returned. */
export function useDocumentAssets(
  projectId: string | undefined,
  documentId: string | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: brainKeys.assets(projectId ?? "", documentId ?? ""),
    enabled: Boolean(projectId && documentId) && options?.enabled !== false,
    queryFn: async (): Promise<DocumentAsset[]> => {
      const res = await api.projects.listAssets({
        params: { id: projectId!, docId: documentId! },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.assets;
    },
  });
}

/**
 * Where `AuthedFrame` / `AuthedImage` fetch the document from.
 *
 * A **path**, not a URL: `authorizedFetch` prefixes `apiBaseUrl`, and it is the
 * only thing that should ever fetch these — the routes are behind `AuthGuard`
 * and a bare `src` sends no bearer token.
 *
 * Off-contract, like `downloadPath`: it answers 302 to a presigned URL. The
 * difference between the two is the disposition — this one asks the browser to
 * *render* the bytes, which is what gives a PDF the native viewer with real
 * pages and search for no bundle size at all.
 */
export {
  documentPreviewPath as previewPath,
  /** One extracted figure or scan, by id. Also a 302, also for an `<img>`. */
  documentAssetContentPath as assetContentPath,
} from "@gb/contracts";

export function useBrainEntry(
  projectId: string | undefined,
  entryId: string | undefined,
) {
  return useQuery({
    queryKey: brainKeys.detail(projectId ?? "", entryId ?? ""),
    enabled: Boolean(projectId && entryId),
    queryFn: async (): Promise<KnowledgeEntry> => {
      const res = await api.brain.get({
        params: { id: projectId!, entryId: entryId! },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.entry;
    },
  });
}

export function useCreateBrainNote(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: KnowledgeNoteInput): Promise<KnowledgeEntry> => {
      const res = await api.brain.create({
        params: { id: projectId },
        body: input,
      });
      if (res.status !== 201) throw new Error(errorMessage(res));
      return res.body.entry;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["project", projectId, "brain"],
      });
    },
  });
}

export function useDeleteBrainEntry(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (entryId: string): Promise<void> => {
      const res = await api.brain.remove({
        params: { id: projectId, entryId },
      });
      // 409 means the row belongs to the ingest path — its message says to
      // delete the document instead, which is more useful than "failed".
      if (res.status !== 204) throw new Error(errorMessage(res));
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["project", projectId, "brain"],
      });
    },
  });
}
